import { useState } from "react";
import { ArrowRight, ArrowLeft, Sparkles, X, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

interface PricingQuizProps {
  isOpen: boolean;
  onClose: () => void;
}

const questions = [
  {
    question: "What type of business are you?",
    options: [
      { label: "Local service business (salon, contractor, tutor)", score: 1 },
      { label: "Growing agency / Consultant / Real estate", score: 2 },
      { label: "Established business (law firm, clinic, B2B)", score: 3 },
      { label: "E-commerce / SaaS / Large organization", score: 4 },
    ],
  },
  {
    question: "How many pages do you need?",
    options: [
      { label: "Up to 4 pages (about, services, contact)", score: 1 },
      { label: "5-7 pages (+ blog, testimonials)", score: 2 },
      { label: "8-10 pages (comprehensive site)", score: 3 },
      { label: "10+ pages or unlimited", score: 4 },
    ],
  },
  {
    question: "What features are most important to you?",
    options: [
      { label: "Gallery, basic SEO, and custom styling", score: 1 },
      { label: "Advanced SEO, blog, and brand customization", score: 2 },
      { label: "Booking system, full customization, priority support", score: 3 },
      { label: "E-commerce, CRM integrations, dedicated manager", score: 4 },
    ],
  },
  {
    question: "What's your primary goal?",
    options: [
      { label: "Showcase my services locally", score: 1 },
      { label: "Generate leads and grow my business", score: 2 },
      { label: "Convert visitors into paying customers", score: 3 },
      { label: "Build a complete digital ecosystem", score: 4 },
    ],
  },
  {
    question: "How soon do you need your site?",
    options: [
      { label: "ASAP - I need it fast!", score: 1 },
      { label: "Within 1-2 weeks", score: 2 },
      { label: "I can wait for quality", score: 3 },
      { label: "Take the time needed for perfection", score: 4 },
    ],
  },
];

const tierRecommendations = [
  {
    tier: 1,
    name: "Essentials Site",
    ourPrice: "$500",
    intakeUrl: "/intake/essentials",
    description: "Great for local businesses that need a solid online foundation.",
  },
  {
    tier: 2,
    name: "Professional Site",
    ourPrice: "$750",
    intakeUrl: "/intake/professional",
    description: "Ideal for businesses ready to grow and generate more leads.",
  },
  {
    tier: 3,
    name: "Business Plus",
    ourPrice: "$1,500",
    intakeUrl: "/intake/business-plus",
    description: "Built for serious businesses that need advanced functionality.",
  },
  {
    tier: 4,
    name: "Premium / Elite",
    ourPrice: "$3k–$5k",
    intakeUrl: "/intake/premium",
    description: "The complete package for brands demanding the best.",
  },
];

const PricingQuiz = ({ isOpen, onClose }: PricingQuizProps) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (score: number) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
  };

  const getRecommendation = () => {
    const avgScore = answers.reduce((a, b) => a + b, 0) / answers.length;
    const tierIndex = Math.min(Math.round(avgScore) - 1, 3);
    return tierRecommendations[Math.max(0, tierIndex)];
  };

  if (!isOpen) return null;

  const recommendation = getRecommendation();
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in overflow-y-auto overscroll-contain touch-pan-y"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden my-6 max-h-[90dvh] flex flex-col min-h-0">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="p-6 pb-4 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-primary" size={24} />
            <h3 className="text-xl font-black">Find Your Perfect Package</h3>
          </div>
          {!showResult && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-muted-foreground mb-2">
                <span>Question {currentQuestion + 1} of {questions.length}</span>
                <span>{Math.round(progress)}% complete</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto flex-1 min-h-0 overscroll-contain scroll-smooth touch-pan-y"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {!showResult ? (
            <div className="animate-fade-in" key={currentQuestion}>
              <p className="text-lg font-bold mb-6">{questions[currentQuestion].question}</p>
              <div className="space-y-3">
                {questions[currentQuestion].options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(option.score)}
                    className="w-full p-4 text-left rounded-lg border border-border bg-background/50 hover:border-primary hover:bg-primary/5 transition-all duration-200 group"
                  >
                    <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>

              {currentQuestion > 0 && (
                <button
                  onClick={goBack}
                  className="mt-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft size={16} />
                  Back to previous question
                </button>
              )}
            </div>
          ) : (
            <div className="animate-scale-in text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
                <Sparkles className="text-primary" size={40} />
              </div>
              <h4 className="text-2xl font-black mb-2">We Recommend</h4>
              <p className="text-3xl font-black text-primary mb-2">
                Tier {recommendation.tier}: {recommendation.name}
              </p>
              <p className="text-2xl font-bold text-green-500 mb-4">
                {recommendation.ourPrice}
              </p>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {recommendation.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={recommendation.intakeUrl}
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary text-primary-foreground font-bold rounded-lg hover:shadow-[0_0_30px_hsl(var(--primary)/0.4)] transition-all duration-300"
                >
                  Get Started
                  <ArrowRight size={18} />
                </Link>
                <button
                  onClick={resetQuiz}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 border-2 border-border text-foreground font-bold rounded-lg hover:border-primary hover:text-primary transition-all duration-300"
                >
                  <RotateCcw size={18} />
                  Retake Quiz
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingQuiz;
